import { useEffect, useState } from 'react';
import audioBus from './audioBus';
import './editorial.css';

/* Compass / atomic-age line marks (stroke = currentColor, no fill). */
const IconListen = ({ playing }) => (
  <svg viewBox="0 0 24 24" aria-hidden="true">
    <circle cx="12" cy="12" r="9" strokeWidth="1.4" />
    {playing
      ? <g strokeWidth="1.6"><line x1="10" y1="8.5" x2="10" y2="15.5" /><line x1="14" y1="8.5" x2="14" y2="15.5" /></g>
      : <path d="M10 8.2 L16 12 L10 15.8 Z" strokeWidth="1.4" strokeLinejoin="round" />}
  </svg>
);
const IconShare = () => (
  <svg viewBox="0 0 24 24" aria-hidden="true" strokeWidth="1.4">
    <circle cx="6" cy="12" r="2.3" /><circle cx="17.5" cy="6" r="2.3" /><circle cx="17.5" cy="18" r="2.3" />
    <line x1="8" y1="10.9" x2="15.5" y2="7.1" /><line x1="8" y1="13.1" x2="15.5" y2="16.9" />
  </svg>
);
const IconPrint = () => (
  <svg viewBox="0 0 24 24" aria-hidden="true" strokeWidth="1.4">
    <path d="M7 9V4h10v5" /><rect x="4.5" y="9" width="15" height="7" rx="1" /><rect x="7.5" y="14" width="9" height="5" />
    <line x1="16.5" y1="11.5" x2="16.5" y2="11.5" strokeWidth="2" strokeLinecap="round" />
  </svg>
);
const IconSave = ({ saved }) => (
  <svg viewBox="0 0 24 24" aria-hidden="true" strokeWidth="1.4" style={{ fill: saved ? 'currentColor' : 'none' }}>
    <path d="M7 4h10v16l-5-3.6L7 20z" strokeLinejoin="round" />
  </svg>
);

// Icon-only buttons with title tooltips + aria-labels for discoverability.
function railButtons({ playing, saved, onListen, onShare, onPrint, onSave, available }) {
  return (
    <>
      <button className="bi-rail-bar__btn" onClick={onListen} aria-pressed={playing}
        title={playing ? 'Pause' : 'Listen'}
        aria-label={playing ? 'Pause audio briefing' : 'Listen to audio briefing'}
        disabled={!available}>
        <IconListen playing={playing} />
      </button>
      <button className="bi-rail-bar__btn" onClick={onShare} title="Share" aria-label="Share and download">
        <IconShare />
      </button>
      <button className="bi-rail-bar__btn" onClick={onPrint} title="Print" aria-label="Print briefing">
        <IconPrint />
      </button>
      <button className="bi-rail-bar__btn" onClick={onSave} aria-pressed={saved}
        title={saved ? 'Saved' : 'Save'} aria-label={saved ? 'Saved' : 'Save briefing'}>
        <IconSave saved={saved} />
      </button>
    </>
  );
}

export default function ActionRail({ shareTitle = 'Bearing Intelligence', shareUrl, saveKey, onSave }) {
  const url = shareUrl || (typeof window !== 'undefined' ? window.location.href : '');
  const key = saveKey || url;
  const [playing, setPlaying] = useState(false);
  const [available, setAvailable] = useState(false);
  const [saved, setSaved] = useState(false);

  useEffect(() => audioBus.subscribe((s) => { setPlaying(s.playing); setAvailable(s.available); }), []);
  useEffect(() => {
    try { setSaved(JSON.parse(localStorage.getItem('bi_saved') || '[]').includes(key)); } catch { /* ignore */ }
  }, [key]);

  const onListen = () => {
    audioBus.reveal(); // expand the scrubber inline
    if (audioBus.getState().available) audioBus.toggle();
  };
  const onShare = async () => {
    try {
      if (navigator.share) { await navigator.share({ title: shareTitle, url }); return; }
      if (navigator.clipboard) { await navigator.clipboard.writeText(url); }
    } catch { /* user cancelled / unsupported */ }
  };
  const onPrint = () => { if (typeof window !== 'undefined') window.print(); };
  const doSave = () => {
    let list = [];
    try { list = JSON.parse(localStorage.getItem('bi_saved') || '[]'); } catch { list = []; }
    const next = list.includes(key) ? list.filter((k) => k !== key) : [...list, key];
    try { localStorage.setItem('bi_saved', JSON.stringify(next)); } catch { /* ignore */ }
    const isSaved = next.includes(key);
    setSaved(isSaved);
    if (onSave) onSave(isSaved);
  };

  const props = { playing, saved, available, onListen, onShare, onPrint, onSave: doSave };
  return (
    <nav className="bi-rail-bar" aria-label="Briefing actions">{railButtons(props)}</nav>
  );
}
